import { ChangeDetectionStrategy, Component } from '@angular/core';
import { atxIsMobile$ } from '@epicuro-next/ataraxia/breakpoint';
import { IMAGE_MAX_SIZES } from '@epicuro-next/ataraxia/image';
import {
  SiteSelectorConfigurationKeys,
  SiteSelectorConfigurations,
  SITE_SELECTOR_NAMESPACE,
} from '@epicuro-next/constants/rule-engine/site-selector-rules';
import { APP_TOP_LEVEL_PATH } from '@epicuro-next/navigation/constants';
import { selectConfigurationValueFrom } from '@epicuro-next/platform/configuration';
import { EnvironmentService } from '@epicuro-next/platform/environment';
import { selectUrl } from '@epicuro-next/state/router-store';
import { selectAccountId } from '@epicuro-next/state/session-state';
import {
  ISite,
  selectBackground,
  selectSitesLoad,
  selectSitesWithSubSites,
  setSelectedSite,
} from '@epicuro-next/state/site-state';
import { selectUserMenuOpened } from '@epicuro-next/state/user-ui-state';
import { isNonNullish, sortSites } from '@epicuro-next/utilities';
import { select, Store } from '@ngrx/store';
import { combineLatest } from 'rxjs';
import { filter, map, pairwise, shareReplay, switchMap } from 'rxjs/operators';

@Component({
  selector: 'epc-site-selector',
  templateUrl: './site-selector.component.html',
  styleUrls: ['./site-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SiteSelectorComponent {
  public imageMaxSizes = IMAGE_MAX_SIZES;
  public sitesSortingOrder$ = this.store.pipe(
    select(
      selectConfigurationValueFrom<
        SiteSelectorConfigurationKeys.SitesOrder,
        SiteSelectorConfigurations
      >(SITE_SELECTOR_NAMESPACE, SiteSelectorConfigurationKeys.SitesOrder, []),
    ),
    filter(isNonNullish),
  );

  public sitesWithSubSites$ = this.store.pipe(select(selectSitesWithSubSites));

  public sites$ = combineLatest([
    this.sitesSortingOrder$,
    this.sitesWithSubSites$,
  ]).pipe(map(([sortingOrder, sites]) => sortSites(sortingOrder, sites)));

  public isLoading$ = this.store.pipe(select(selectSitesLoad));
  public background$ = this.store.pipe(select(selectBackground));
  public isMobile$ = atxIsMobile$;
  public multiSiteSupport$ = this.environmentService.environmentSettings.pipe(
    map((settings) => !!JSON.parse(settings.multiSiteSupport)),
  );
  public isInSiteSelector$ = this.store.pipe(
    select(selectUrl),
    map(
      (url) => url.replace(/\//g, '') === APP_TOP_LEVEL_PATH.siteSelector.path,
    ),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  public isAccountChangedInSiteSelector$ = this.isInSiteSelector$.pipe(
    filter(Boolean),
    switchMap(() => this.store.pipe(select(selectAccountId))),
    pairwise(),
    map(([oldAccountId, newAccountId]) => oldAccountId !== newAccountId),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  public isUserMenuOpened$ = this.store.pipe(
    select(selectUserMenuOpened),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  public openSiteSelectorManually$ = combineLatest([
    this.isUserMenuOpened$,
    this.isAccountChangedInSiteSelector$,
  ]).pipe(
    map(([isUserMenuOpened, isAccountChangedInSelector]) =>
      isUserMenuOpened ? false : isAccountChangedInSelector,
    ),
  );

  constructor(
    private store: Store,
    private environmentService: EnvironmentService,
  ) {}

  public onSelectSite(site: ISite) {
    this.store.dispatch(setSelectedSite({ site }));
  }
}
